import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useProjectContext } from '../contexts/ProjectContext';
import { useUser } from '../contexts/UserContext';

// Icons
import { ReactComponent as ChevronDown } from '../assets/icons/chevron-down.svg';
import { ReactComponent as Settings } from '../assets/icons/settings.svg';
import { ReactComponent as AibexColorSideLogo } from '../assets/icons/aibex-side-logo-color.svg';
import styles from '../assets/styles/components/Header.module.scss';

interface HeaderBudgetSummary {
  total_budget: number;
  current_spend: number;
  progress_rate: number;
}

const EMPTY_BUDGET_SUMMARY: HeaderBudgetSummary = {
  total_budget: 0,
  current_spend: 0,
  progress_rate: 0,
};

const formatManYen = (amount: number): string => {
  const manYen = amount / 10000;

  if (Number.isInteger(manYen)) {
    return `${manYen.toLocaleString()}万円`;
  }

  return `${manYen.toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}万円`;
};

const formatProgressRate = (progressRate: number): string => {
  return `${(progressRate * 100).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
};

const isBudgetConfigured = (budgetSummary: HeaderBudgetSummary): boolean => {
  return budgetSummary.total_budget > 0;
};

const Header: React.FC = () => {
  const { user } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [budgetSummary, setBudgetSummary] = useState<HeaderBudgetSummary>(EMPTY_BUDGET_SUMMARY);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userIconTriggerRef = useRef<HTMLDivElement>(null);

  const { projectList, selectedProjectId, selectedProjectName, setSelectedProject } = useProjectContext();

  const navigate = useNavigate();
  const location = useLocation();

  // Click outside to close user menu popover
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isUserMenuOpen &&
        userMenuRef.current &&
        !userMenuRef.current.contains(e.target as Node) &&
        userIconTriggerRef.current &&
        !userIconTriggerRef.current.contains(e.target as Node)
      ) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isUserMenuOpen]);

  useEffect(() => {
    const fetchHeaderBudgetSummary = async () => {
      if (!selectedProjectId) {
        setBudgetSummary(EMPTY_BUDGET_SUMMARY);
        return;
      }

      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/get-header-budget-summary?project_id=${selectedProjectId}`,
          {
            credentials: 'include',
          },
        );

        if (!res.ok) {
          throw new Error('Failed to fetch header budget summary');
        }

        const data: HeaderBudgetSummary = await res.json();
        setBudgetSummary({
          total_budget: Number(data.total_budget) || 0,
          current_spend: Number(data.current_spend) || 0,
          progress_rate: Number(data.progress_rate) || 0,
        });
      } catch (error) {
        console.error('Header budget summary fetch error:', error);
        setBudgetSummary(EMPTY_BUDGET_SUMMARY);
      }
    };

    void fetchHeaderBudgetSummary();
  }, [selectedProjectId]);

  const handleLogoutClick = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        window.location.replace('/login?status=loggedout');
      } else {
        alert('ログアウトに失敗しました。');
      }
    } catch (error) {
      console.error('Logout Error:', error);
    }
  };

  const handleMenuClick = (path: string) => {
    if (path === 'account') {
      navigate(`/account-setting/profile`);
    } else {
      navigate(`/${path}`);
    }
    setIsUserMenuOpen(false);
  };

  const handleClickMenu = (path: string) => {
    navigate(`/${path}`);
  };

  const handleSelectProject = (id: number, name: string) => {
    setSelectedProject(id, name);
    setIsOpen(false);
  };

  const handleBudgetSettingClick = () => {
    navigate('/project-setting/budget');
  };

  return (
    <header className={styles.header}>
      {/* 左セクション: ロゴ + PJプルダウン (プルダウン背景つき・枠なし) + 予算表示 */}
      <div className={styles.headerLeft}>
        <a href="/" className={styles.logo} title="Aibex Home">
          <AibexColorSideLogo />
        </a>

        {/* PJ切り替えプルダウン */}
        <div className={styles.pjDropdownWrap}>
          <div
            className={`${styles.pjDropdown} ${isOpen ? styles.open : ''}`}
            onClick={() => setIsOpen(!isOpen)}
          >
            <span>{selectedProjectName || 'プロジェクト選択'}</span>
            <span className={`${styles.pjDropdownArrow} ${isOpen ? styles.open : ''}`}>
              <ChevronDown />
            </span>
          </div>
          {isOpen && (
            <div className={styles.pjDropdownListWrap}>
              <ul className={styles.pjDropdownList}>
                <li className={styles.pjAddProjectItem}>
                  <button
                    type="button"
                    className={styles.pjAddProjectButton}
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/project-create');
                    }}
                  >
                    ＋ プロジェクトを追加
                  </button>
                </li>
                {projectList.map((adv) => (
                  <li key={adv.id} onClick={() => handleSelectProject(adv.id, adv.projectName)}>
                    {adv.projectName}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* 予算 & 実績表示 */}
        <div className={styles.budgetDisplay}>
          <span className={styles.separator}>｜</span>
          {isBudgetConfigured(budgetSummary) ? (
            <span className={styles.total}>
              予算：{formatManYen(budgetSummary.total_budget)} / {formatManYen(budgetSummary.current_spend)} （{formatProgressRate(budgetSummary.progress_rate)}消化）
            </span>
          ) : (
            <>
              <span className={styles.total}>予算：</span>
              <button
                type="button"
                className={styles.budgetUnsetButton}
                onClick={handleBudgetSettingClick}
              >
                予算未設定
              </button>
            </>
          )}
        </div>
      </div>

      {/* 右セクション: リンク3種 + 既存の歯車設定アイコン + ユーザーアバター */}
      <div className={styles.headerRight}>
        <div className={styles.headerLinksWrap}>
          <span
            className={`${styles.headerLink} ${location.pathname.startsWith('/board') ? styles.active : ''}`}
            onClick={() => handleClickMenu('board')}
          >
            新しいチャット
          </span>
          <span
            className={`${styles.headerLink} ${location.pathname === '/action-history' ? styles.active : ''}`}
            onClick={() => handleClickMenu('action-history')}
          >
            施策一覧
          </span>
          <span
            className={`${styles.headerLink} ${location.pathname.startsWith('/report') ? styles.active : ''}`}
            onClick={() => handleClickMenu('report')}
          >
            レポート
          </span>
        </div>

        {/* アバターすぐ左：プロジェクト設定用の歯車アイコン */}
        <button
          type="button"
          className={styles.settingBtn}
          onClick={() => handleClickMenu('project-setting/preference')}
          title="プロジェクト設定"
        >
          <Settings />
        </button>

        <div className={styles.userMenuContainer}>
          <div
            ref={userIconTriggerRef}
            className={styles.userAvatar}
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            title="プロフィールと設定"
          >
            {user?.last_name ? user.last_name[0] : 'U'}
          </div>

          {isUserMenuOpen && (
            <div className={styles.userPopover} ref={userMenuRef}>
              <div className={styles.userInfoWrap}>
                <div className={styles.userName}>{user?.last_name || 'ユーザー'}</div>
                <div className={styles.userEmail}>{user?.email || ''}</div>
              </div>
              <div className={styles.popoverDividerLine} />
              <ul className={styles.settingMenu}>
                <li onClick={() => handleMenuClick('account')}>アカウント設定</li>
              </ul>
              {user?.is_admin && (
                <>
                  <div className={styles.popoverDividerLine} />
                  <ul className={styles.settingMenu}>
                    <li onClick={() => handleMenuClick('admin')}>管理者ポータルを開く</li>
                  </ul>
                </>
              )}
              <div className={styles.popoverDividerLine} />
              <ul className={styles.settingMenu}>
                <li onClick={() => handleMenuClick('account')}>ヘルプ</li>
                <li onClick={handleLogoutClick}>ログアウト</li>
              </ul>
              {user?.is_admin && (
                <>
                  <div className={styles.popoverDividerLine} />
                  <ul className={styles.settingMenu}>
                    <li onClick={() => handleMenuClick('devlink')}>検証リンクリスト</li>
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
